import React, { useState } from 'react';
import { ChevronUpIcon } from '@heroicons/react/24/outline';

export interface AccordionProps {
  /** Section title, shown next to the optional numbered badge. */
  title: React.ReactNode;
  /** Numbered pill rendered before the title, e.g. "01". Omit for no badge. */
  badgeNumber?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  testId?: string;
}

/**
 * Bordered card with a header row (numbered badge + title + chevron) and a
 * collapsible content area — each instance owns its own expand/collapse
 * state independently, so multiple accordions on the same page open and
 * close without affecting one another (not a single-open list).
 */
export const Accordion: React.FC<AccordionProps> = ({
  title,
  badgeNumber,
  children,
  defaultExpanded = true,
  testId,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div
      className="w-full rounded-[12px] border border-primary-color-90 bg-white overflow-hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        className="flex w-full cursor-pointer items-center justify-between bg-neutral-90 p-4"
      >
        <div className="flex items-center gap-3">
          {badgeNumber !== undefined && (
            <span className="rounded-[4px] bg-primary-color-40 px-2 py-1 text-[11px] font-medium leading-4 tracking-[0.5px] text-white">
              {badgeNumber}
            </span>
          )}
          <span className="text-[14px] font-semibold leading-5 tracking-[0.1px] text-primary-color-30">
            {title}
          </span>
        </div>
        <ChevronUpIcon
          className={`h-[18px] w-[18px] shrink-0 text-neutral-10 transition-transform ${
            isExpanded ? '' : 'rotate-180'
          }`}
        />
      </button>
      {isExpanded && <div className="flex flex-col gap-6 p-6">{children}</div>}
    </div>
  );
};

export default Accordion;
